import { useState } from 'react';
import { useRouter } from 'next/router';
import { useInstitutionalPage } from '@/contexts/InstitutionalPageContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { IReviews } from '@/types/api/companies';
import {
  ReviewsDefault,
  totalAverageReviews,
} from '@/utils/components/institutionalPage';
import { formatDifferenceBetweenDates } from '@/utils/dateUtils';
import { handleErrorAsync } from '@/utils/handlers';
import { KeyboardArrowDown, Star } from '@mui/icons-material';
import {
  Box,
  Card,
  FormControl,
  LinearProgress,
  MenuItem,
  Rating,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

interface IReviewsProps {
  reviews: IReviews[];
  reviewsWithParams?: IReviews[];
  setReviews: (results: IReviews[]) => void;
}

interface IReviewsProgressProps {
  ratingNumber: number;
  quantityRating: number;
  quantityReviews: number;
}

const ReviewsProgress = ({
  ratingNumber,
  quantityRating,
  quantityReviews,
}: IReviewsProgressProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Typography>{ratingNumber}</Typography>
      <Star sx={{ color: '#FAAF00' }} />
      <Box sx={{ width: '100%' }}>
        <LinearProgress
          sx={{
            height: '6px',
            borderRadius: '10px',
            backgroundColor: '#D9D9D9',
            width: '100%',
            '& .MuiLinearProgress-barColorPrimary': {
              backgroundColor: '#FAAF00',
            },
          }}
          value={(quantityRating / quantityReviews) * 100}
          variant="determinate"
        />
      </Box>
    </Box>
  );
};

const renderReview = (review: IReviews, index: number) => (
  <Card
    key={index}
    sx={{
      p: 2,
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      my: 2,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="subtitle2">{review.name}</Typography>
      <Typography variant="caption" color="#808080">
        {formatDifferenceBetweenDates(
          review.created_at,
          new Date().toString(),
          1
        )}
      </Typography>
    </Box>
    <Rating
      value={review.rating}
      precision={0.5}
      readOnly
      size="small"
      sx={{
        '& .MuiRating-icon': {
          color: '#FAAF00',
        },
      }}
    />
    <Typography variant="caption">&quot;{review.comment}&quot;</Typography>
  </Card>
);

export const ReviewsPortfolio = ({
  reviews,
  reviewsWithParams,
  setReviews,
}: IReviewsProps) => {
  const router = useRouter();
  const { setIsLoading } = useLoading();

  const defaultReviewsValue = ReviewsDefault(reviews);

  const { company } = useInstitutionalPage();

  const hasAvaliationRouter = /avaliacoes/.test(router.route);

  const [quickFilterSelected, setQuickFilterSelected] = useState(0);

  const menuQuickFilters = [
    {
      value: 0,
      name: 'Todas',
    },
    {
      value: 7,
      name: 'Últimos 7 dias',
    },
    {
      value: 14,
      name: 'Últimos 14 dias',
    },
    {
      value: 30,
      name: 'Últimos 30 dias',
    },
  ];

  const handleQuickFilter = async (event: SelectChangeEvent<number>) => {
    const date = new Date();
    const dateFilter = new Date(
      date.setDate(date.getDate() - Number(event.target.value))
    ).toJSON();
    setQuickFilterSelected(Number(event.target.value));

    const [reviewsData, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const { data } = await api.get<{
        results: IReviews[];
      }>(
        `/api/companies/${company.id}/reviews/`,
        Number(event.target.value) > 0
          ? {
              params: {
                created_at__gte: dateFilter,
              },
            }
          : {}
      );
      return data;
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    setReviews(reviewsData.results);
  };

  const quantityRepeatedElements = (
    reviewsArray: IReviews[],
    elementRating: number
  ) => {
    return reviewsArray.reduce((acc, item) => {
      if (item.rating === elementRating) {
        return acc + 1;
      } else {
        return acc;
      }
    }, 0);
  };
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 4,
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h3">
            {totalAverageReviews(ReviewsDefault(reviews))
              .toFixed(1)
              .replace('.', ',')}
          </Typography>
          <Rating
            value={totalAverageReviews(ReviewsDefault(reviews))}
            precision={0.1}
            readOnly
            size="small"
            sx={{
              '& .MuiRating-icon': {
                color: '#FAAF00',
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '80%',
          }}
        >
          <ReviewsProgress
            ratingNumber={5}
            quantityRating={quantityRepeatedElements(defaultReviewsValue, 5)}
            quantityReviews={defaultReviewsValue.length}
          />
          <ReviewsProgress
            ratingNumber={4}
            quantityRating={quantityRepeatedElements(defaultReviewsValue, 4)}
            quantityReviews={defaultReviewsValue.length}
          />
          <ReviewsProgress
            ratingNumber={3}
            quantityRating={quantityRepeatedElements(defaultReviewsValue, 3)}
            quantityReviews={defaultReviewsValue.length}
          />
          <ReviewsProgress
            ratingNumber={2}
            quantityRating={quantityRepeatedElements(defaultReviewsValue, 2)}
            quantityReviews={defaultReviewsValue.length}
          />
          <ReviewsProgress
            ratingNumber={1}
            quantityRating={quantityRepeatedElements(defaultReviewsValue, 1)}
            quantityReviews={defaultReviewsValue.length}
          />
        </Box>
      </Box>
      {hasAvaliationRouter && (
        <Card
          sx={{
            backgroundColor: 'background.default',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            width: 'auto',
            mb: 3,
          }}
        >
          <FormControl
            sx={{
              minWidth: 120,
              '& .Mui-selected': {
                backgroundColor: 'red',
              },
            }}
            size="small"
          >
            <Select
              color="borderSelect"
              onChange={e => handleQuickFilter(e)}
              value={quickFilterSelected}
              renderValue={quickFilterSelected => {
                if (quickFilterSelected === 0) {
                  return <Typography variant="body2">Todas</Typography>;
                }

                return (
                  <Typography variant="body2">
                    Últimos {quickFilterSelected} dias
                  </Typography>
                );
              }}
              sx={{
                borderRadius: 5,
                fontSize: '13px',
                height: '40px',
                backgroundColor: quickFilterSelected
                  ? 'primary.main'
                  : '#FFFFFF',
                color: quickFilterSelected ? '#FFFFFF' : '#808080',
                justifyContent: 'flex-start',
                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
              }}
              IconComponent={KeyboardArrowDown}
              MenuProps={{
                MenuListProps: {
                  disablePadding: true,
                },
              }}
            >
              {menuQuickFilters.map(menuFilter => (
                <MenuItem
                  key={menuFilter.value}
                  value={menuFilter.value}
                  sx={{ backgroundColor: '#F4F4F4' }}
                >
                  <Typography variant="body2">{menuFilter.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Card>
      )}
      {reviewsWithParams
        ? reviewsWithParams.map(renderReview)
        : reviews.map(renderReview)}
    </>
  );
};
